import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { API_URL, formatDate } from "../../lib/utils";
import {
    Building2,
    Users,
    Activity,
    ArrowRight,
    FolderKanban,
    Receipt,
    BarChart3,
    TrendingUp,
    Clock,
    UserPlus,
    RefreshCw,
} from "lucide-react";
import { Button } from "../../components/ui/button";

export const AdminDashboardPage = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const results = await Promise.allSettled([
            fetch(`${API_URL}/platform/dashboard`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/platform/stats`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/platform/activity`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
        ]);
        if (results[0].status === "fulfilled") setData(results[0].value);
        if (results[1].status === "fulfilled") setStats(results[1].value);
        if (results[2].status === "fulfilled") setActivity(results[2].value);
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
        );
    }

    const { tenants = [] } = data || {};

    return (
        <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Vue d'ensemble
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Gestion globale de la plateforme
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-dashboard-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </div>

            {/* Main Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card className="border-violet-200 dark:border-violet-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats?.total_tenants || 0}</p>
                                <p className="text-xs text-muted-foreground">Entreprises</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-blue-200 dark:border-blue-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats?.active_tenants || 0}</p>
                                <p className="text-xs text-muted-foreground">Actifs</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-green-200 dark:border-green-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <Users className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                                <p className="text-xs text-muted-foreground">Utilisateurs</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-orange-200 dark:border-orange-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                <FolderKanban className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats?.total_projects || 0}</p>
                                <p className="text-xs text-muted-foreground">Projets</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-emerald-200 dark:border-emerald-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{(stats?.total_revenue || 0).toLocaleString()} €</p>
                                <p className="text-xs text-muted-foreground">Revenus</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-amber-200 dark:border-amber-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <Receipt className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats?.total_invoices || 0}</p>
                                <p className="text-xs text-muted-foreground">Factures</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tenants List */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="font-heading">Entreprises récentes</CardTitle>
                                <CardDescription>Dernières inscriptions sur la plateforme</CardDescription>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/tenants")} data-testid="view-all-tenants-btn">
                                Voir tout <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {tenants.slice(0, 5).map((t) => (
                                <div
                                    key={t.tenant_id}
                                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                                    onClick={() => navigate("/admin/tenants")}
                                    data-testid={`tenant-row-${t.tenant_id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                            <Building2 className="w-5 h-5 text-violet-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{t.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {t.user_count} utilisateur{t.user_count > 1 ? "s" : ""} · {t.project_count || 0} projet{(t.project_count || 0) > 1 ? "s" : ""} · {t.client_count || 0} client{(t.client_count || 0) > 1 ? "s" : ""}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant={t.is_active !== false ? "default" : "destructive"}>
                                            {t.is_active !== false ? "Actif" : "Inactif"}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs capitalize">{t.plan || "free"}</Badge>
                                    </div>
                                </div>
                            ))}
                            {tenants.length === 0 && (
                                <p className="text-center text-muted-foreground py-6">Aucune entreprise inscrite</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Activity Feed */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Clock className="w-5 h-5 text-violet-500" />
                            Activité récente
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {activity.slice(0, 6).map((a, i) => (
                                <div key={i} className="flex items-start gap-3 pb-3 border-b last:border-0 last:pb-0">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                        a.type === "tenant_created" 
                                            ? "bg-violet-100 dark:bg-violet-900/30" 
                                            : "bg-blue-100 dark:bg-blue-900/30"
                                    }`}>
                                        {a.type === "tenant_created" ? (
                                            <Building2 className="w-4 h-4 text-violet-500" />
                                        ) : (
                                            <UserPlus className="w-4 h-4 text-blue-500" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{a.title}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {a.timestamp ? formatDate(a.timestamp) : "-"}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {activity.length === 0 && (
                                <p className="text-center text-muted-foreground py-4 text-sm">Aucune activité récente</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Plan Distribution */}
                {stats?.plans_distribution && Object.keys(stats.plans_distribution).length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-violet-500" />
                                Distribution des plans
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {Object.entries(stats.plans_distribution).map(([plan, count]) => {
                                    const total = Object.values(stats.plans_distribution).reduce((a, b) => a + b, 0);
                                    const percentage = total > 0 ? (count / total) * 100 : 0;
                                    const colors = {
                                        free: "bg-gray-500",
                                        basic: "bg-blue-500",
                                        pro: "bg-violet-500",
                                        enterprise: "bg-amber-500",
                                    };
                                    return (
                                        <div key={plan} className="space-y-1">
                                            <div className="flex justify-between text-sm">
                                                <span className="capitalize font-medium">{plan}</span>
                                                <span className="text-muted-foreground">{count} ({percentage.toFixed(0)}%)</span>
                                            </div>
                                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${colors[plan] || "bg-violet-500"} transition-all duration-500`}
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Monthly Registrations */}
                {stats?.monthly_registrations && stats.monthly_registrations.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-emerald-500" />
                                Inscriptions mensuelles
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-end gap-3 h-40">
                                {stats.monthly_registrations.map((m, i) => {
                                    const maxCount = Math.max(...stats.monthly_registrations.map(x => x.count), 1);
                                    const height = (m.count / maxCount) * 100;
                                    return (
                                        <div key={i} className="flex-1 flex flex-col items-center gap-2">
                                            <span className="text-sm font-semibold text-violet-600">{m.count}</span>
                                            <div
                                                className="w-full bg-gradient-to-t from-violet-500 to-violet-400 rounded-t transition-all duration-500"
                                                style={{ height: `${Math.max(height, 8)}%` }}
                                            />
                                            <span className="text-xs text-muted-foreground">{m.month.split(" ")[0]}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Quick Actions */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Actions rapides</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-3">
                        <Button variant="outline" onClick={() => navigate("/admin/tenants")} data-testid="quick-action-tenants">
                            <Building2 className="w-4 h-4 mr-2" />
                            Gérer les entreprises
                        </Button>
                        <Button variant="outline" onClick={() => navigate("/admin/settings")} data-testid="quick-action-settings">
                            <Activity className="w-4 h-4 mr-2" />
                            Configuration
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
